import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { linkify } from "@/lib/linkify";
import CustomerHistoryPanel from "@/components/CustomerHistoryPanel";
import FormSummaryCard from "@/components/FormSummaryCard";
import { PartsQuoteCard } from "@/components/PartsQuoteCard";
import { AppointmentRequestCard } from "@/components/AppointmentRequestCard";
import { PriorCasesCard } from "@/components/PriorCasesCard";
import { CustomerVehicles } from "@/components/CustomerVehicles";
import { AfterHoursContactCard } from "@/components/AfterHoursContactCard";
import ConversationTagsEditor from "@/components/ConversationTagsEditor";
import MessageTemplatesPicker from "@/components/MessageTemplatesPicker";
import EscalateDialog from "@/components/EscalateDialog";
import { AccountTag } from "@/components/AccountTag";
import AttachmentButton, { AttachmentChip, AttachmentPreview } from "@/components/AttachmentButton";
import NextContactPanel from "@/components/NextContactPanel";
import SdmLogo from "@/components/SdmLogo";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  WhatsappLogo, FacebookLogo, InstagramLogo, EnvelopeSimple, Globe,
  Sparkle, PaperPlaneRight, CheckCircle, WarningOctagon, ChatCircle,
  User as UserIcon, Robot, Pause, Play, MagnifyingGlass, XCircle,
} from "@phosphor-icons/react";

const CHANNEL_ICONS = {
  whatsapp: { icon: WhatsappLogo, color: "text-emerald-500" },
  messenger: { icon: FacebookLogo, color: "text-blue-500" },
  instagram: { icon: InstagramLogo, color: "text-pink-500" },
  email: { icon: EnvelopeSimple, color: "text-amber-500" },
  web: { icon: Globe, color: "text-primary" },
};

const STATUS_META = {
  open:       { label: "Abierta",    className: "border-sky-500/40 text-sky-500 bg-sky-500/10" },
  assigned:   { label: "Asignada",   className: "border-blue-500/40 text-blue-500 bg-blue-500/10" },
  escalated:  { label: "Escalada",   className: "border-amber-500/50 text-amber-600 bg-amber-500/10" },
  resolved:   { label: "Resuelta",   className: "border-emerald-500/40 text-emerald-500 bg-emerald-500/10" },
  abandoned:  { label: "Abandonada", className: "border-rose-500/40 text-rose-500 bg-rose-500/10" },
  closed:     { label: "Cerrada",    className: "border-slate-500/30 text-slate-400 bg-slate-500/10" },
};

function ChannelBadge({ channel }) {
  const cfg = CHANNEL_ICONS[channel] || CHANNEL_ICONS.web;
  const Icon = cfg.icon;
  return <Icon size={14} weight="fill" className={cfg.color} />;
}

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

// Cards from a case closed before the latest reopen belong to "Casos anteriores", not to the new case.
const currentCase = (conv, at) => !conv?.reopened_at || !at || at >= conv.reopened_at;

export default function Inbox() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialConvId = searchParams.get("conv");
  const [conversations, setConversations] = useState([]);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("filter") === "pending" ? "pending" : "all");
  const [searchQ, setSearchQ] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all"); // all|today|7d|30d
  const [scope, setScope] = useState("all"); // all|mine
  const [agents, setAgents] = useState([]);
  const [selectedId, setSelectedId] = useState(initialConvId || null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalatePerm, setEscalatePerm] = useState({ allowed: true, reason: null });
  useEffect(() => {
    api.get("/me/escalation").then(({ data }) => setEscalatePerm(data)).catch(() => {});
  }, [selectedId]);
  // Sprint 35B — Timeline por canal: filtra el hilo por canal.
  // null = ver todos; "whatsapp"|"web"|"messenger"|"instagram"|"email" = solo ese.
  const [channelFilter, setChannelFilter] = useState(null);
  const scrollRef = useRef(null);

  const loadConvs = useCallback(async () => {
    const params = new URLSearchParams();
    if (statusFilter === "pending") params.set("pending", "true");
    else if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
    if (searchQ.trim()) params.set("q", searchQ.trim());
    // "mine" scope takes precedence over the free-form agent filter.
    const effectiveAgent = scope === "mine" ? user?.id : (agentFilter !== "all" ? agentFilter : null);
    if (effectiveAgent) params.set("assigned_to", effectiveAgent);
    if (dateRange !== "all") {
      const now = new Date();
      const from = new Date(now);
      if (dateRange === "today") from.setHours(0, 0, 0, 0);
      else if (dateRange === "7d") from.setDate(now.getDate() - 7);
      else if (dateRange === "30d") from.setDate(now.getDate() - 30);
      params.set("date_from", from.toISOString());
    }
    const qs = params.toString();
    const { data } = await api.get(`/conversations${qs ? `?${qs}` : ""}`);
    setConversations(data);
    setSelectedId((cur) => cur || (data[0]?.id ?? null));
  }, [statusFilter, searchQ, agentFilter, dateRange, scope, user?.id]);

  useEffect(() => {
    const timer = setTimeout(loadConvs, 300);
    // Live refresh of the conversation list every 15s so new inbound
    // messages reorder the sidebar without a page refresh.
    const iv = setInterval(loadConvs, 15000);
    return () => { clearTimeout(timer); clearInterval(iv); };
  }, [loadConvs]);

  useEffect(() => {
    api.get("/users?role=sales,bdc,supervisor").then((r) => setAgents(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        const r = await api.get(`/conversations/${selectedId}`);
        if (!cancelled) {
          setDetail((prev) => {
            // Avoid flicker: if the message count + last message id are
            // identical, keep the previous reference so React skips the
            // re-render and the user's textarea / scroll stays put.
            const nextMsgs = r.data?.messages || [];
            const prevMsgs = prev?.messages || [];
            if (
              prev?.conversation?.id === r.data?.conversation?.id &&
              prevMsgs.length === nextMsgs.length &&
              (prevMsgs[prevMsgs.length - 1]?.id || "") === (nextMsgs[nextMsgs.length - 1]?.id || "") &&
              prev?.assigned_agent?.id === r.data?.assigned_agent?.id &&
              prev?.conversation?.status === r.data?.conversation?.status
            ) {
              return prev;
            }
            return r.data;
          });
        }
      } catch (_) { /* swallow — next tick retries */ }
    };
    fetchDetail();
    setSuggestions([]);
    setPendingAttachments([]);
    setChannelFilter(null);  // Sprint 35B — reset timeline filter on switch
    // Live refresh every 6s while this conversation is open.
    const iv = setInterval(fetchDetail, 6000);
    // Sync ?conv=… so this view is deep-linkable from notifications.
    if (searchParams.get("conv") !== selectedId) {
      const next = new URLSearchParams(searchParams);
      next.set("conv", selectedId);
      setSearchParams(next, { replace: true });
    }
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedId, searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("filter") === "pending") setStatusFilter("pending");
  }, [searchParams]);

  // If a ?conv=… deep-link points to a conversation that doesn't match the
  // current filter tab, widen the filter to 'all' so the user actually sees it.
  useEffect(() => {
    if (!initialConvId || !conversations.length) return;
    const present = conversations.some((c) => c.id === initialConvId);
    if (!present && statusFilter !== "all") {
      setStatusFilter("all");
    }
  }, [conversations, initialConvId, statusFilter]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail?.messages?.length]);

  const send = async () => {
    if ((!reply.trim() && pendingAttachments.length === 0) || !selectedId) return;
    try {
      await api.post(`/conversations/${selectedId}/messages`, {
        content: reply || (pendingAttachments.length ? "📎 Archivo adjunto" : ""),
        sender_type: "agent",
        attachments: pendingAttachments.map((a) => a.url),
      });
      setReply("");
      setPendingAttachments([]);
      const { data } = await api.get(`/conversations/${selectedId}`);
      setDetail(data);
      loadConvs();
    } catch (e) {
      toast.error("Error enviando mensaje");
    }
  };

  const doSuggest = async () => {
    setSuggestLoading(true);
    try {
      const { data } = await api.post(`/conversations/${selectedId}/suggest`);
      setSuggestions(data.suggestions || []);
    } catch {
      toast.error("Error generando sugerencias");
    } finally {
      setSuggestLoading(false);
    }
  };

  const toggleBot = async () => {
    if (!selectedId || !detail?.conversation) return;
    const paused = !detail.conversation.bot_paused;
    try {
      await api.post(`/conversations/${selectedId}/bot-toggle`, { paused });
      toast.success(paused ? "Bot pausado — la IA no responderá" : "Bot reactivado — la IA volverá a responder");
      const { data } = await api.get(`/conversations/${selectedId}`);
      setDetail(data);
      loadConvs();
    } catch {
      toast.error("Error cambiando el estado del bot");
    }
  };

  const doSummarize = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await api.post(`/conversations/${selectedId}/summarize`);
      setDetail((d) => ({ ...d, conversation: { ...d.conversation, summary: data.summary } }));
      toast.success("Resumen generado");
    } catch {
      toast.error("Error resumiendo");
    } finally {
      setSummaryLoading(false);
    }
  };

  const updateStatus = async (status) => {
    await api.patch(`/conversations/${selectedId}`, { status });
    toast.success(`Estado: ${status}`);
    loadConvs();
    const { data } = await api.get(`/conversations/${selectedId}`);
    setDetail(data);
  };

  const onEscalated = async () => {
    loadConvs();
    if (selectedId) {
      const { data } = await api.get(`/conversations/${selectedId}`);
      setDetail(data);
    }
  };

  const onKeyDown = (e) => {
    // Enter -> send. Shift+Enter -> newline. Cmd/Ctrl+Enter -> send.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <Topbar title={t("inbox.title")} subtitle={t("inbox.subtitle")} />
      <div className="flex flex-1 min-h-0" data-testid="inbox-layout">
        {/* Conversations list */}
        <div className="w-96 flex-shrink-0 border-r border-border flex flex-col bg-card min-h-0">
          <div className="p-4 border-b border-border space-y-2">
            {user?.id && (
              <div className="flex gap-1.5" data-testid="inbox-scope-toggle">
                <button
                  type="button"
                  onClick={() => setScope("all")}
                  data-testid="scope-all"
                  className={`flex-1 px-2 h-8 rounded-md text-[11px] font-medium border transition-colors ${
                    scope === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground border-input hover:bg-secondary"
                  }`}
                >
                  {lang === "es" ? "Todas" : "All"}
                </button>
                <button
                  type="button"
                  onClick={() => setScope("mine")}
                  data-testid="scope-mine"
                  className={`flex-1 px-2 h-8 rounded-md text-[11px] font-semibold border transition-colors flex items-center justify-center gap-1 ${
                    scope === "mine"
                      ? "bg-blue-500 text-white border-blue-500 shadow-sm"
                      : "bg-background text-muted-foreground border-input hover:bg-secondary"
                  }`}
                >
                  <UserIcon size={11} weight="fill" />
                  {lang === "es" ? `Mías (${user.name?.split(" ")[0] || "yo"})` : `Mine (${user.name?.split(" ")[0] || "me"})`}
                </button>
              </div>
            )}
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder={lang === "es" ? "Buscar cliente, teléfono o mensaje…" : "Search customer, phone, message…"}
                className="pl-8 h-9 text-sm"
                data-testid="inbox-search"
              />
              {searchQ && (
                <button
                  onClick={() => setSearchQ("")}
                  className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                  data-testid="inbox-search-clear"
                >
                  <XCircle size={14} weight="fill" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                disabled={scope === "mine"}
                className="h-8 rounded-md border border-input bg-background px-2 text-[11px] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="inbox-agent-filter"
              >
                <option value="all">{lang === "es" ? "Todos los agentes" : "All agents"}</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-[11px]"
                data-testid="inbox-date-filter"
              >
                <option value="all">{lang === "es" ? "Cualquier fecha" : "Any date"}</option>
                <option value="today">{lang === "es" ? "Hoy" : "Today"}</option>
                <option value="7d">{lang === "es" ? "Últimos 7 días" : "Last 7 days"}</option>
                <option value="30d">{lang === "es" ? "Últimos 30 días" : "Last 30 days"}</option>
              </select>
            </div>
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList className="grid grid-cols-7 w-full h-9">
                <TabsTrigger value="all" data-testid="filter-all" className="text-[10px]">{t("inbox.all")}</TabsTrigger>
                <TabsTrigger value="open" data-testid="filter-open" className="text-[10px]">{t("inbox.open")}</TabsTrigger>
                <TabsTrigger value="escalated" data-testid="filter-escalated" className="text-[10px]">{t("inbox.escalated")}</TabsTrigger>
                <TabsTrigger value="resolved" data-testid="filter-resolved" className="text-[10px]">{t("inbox.resolved")}</TabsTrigger>
                <TabsTrigger value="abandoned" data-testid="filter-abandoned" className="text-[10px]">Aband.</TabsTrigger>
                <TabsTrigger value="closed" data-testid="filter-closed" className="text-[10px]">Cerradas</TabsTrigger>
                <TabsTrigger value="pending" data-testid="filter-pending" className="text-[10px]">Pend.</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <ScrollArea className="flex-1 min-h-0">
            {conversations.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">{t("common.empty")}</div>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                data-testid={`conv-item-${c.id}`}
                className={`w-full overflow-hidden text-left px-4 py-3 border-b border-border transition-colors ${
                  selectedId === c.id ? "bg-accent" : "hover:bg-secondary"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                    {c.customer?.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate flex items-center gap-1.5">
                        <ChannelBadge channel={c.channel} />
                        {c.customer?.name || "Cliente"}
                        <AccountTag channel={c.channel} accountId={c.channel_account_id} testId={`conv-account-${c.id}`} />
                      </div>
                      <div className="text-[10px] text-muted-foreground shrink-0">{timeAgo(c.last_message_at)}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2 [overflow-wrap:anywhere]">
                      {c.last_message_preview}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {c.unread && <span className="w-1.5 h-1.5 rounded-full bg-primary" data-testid="unread-dot" />}
                      {STATUS_META[c.status] && (
                        <Badge
                          variant="outline"
                          className={`text-[9px] px-1.5 py-0 h-4 uppercase tracking-wider ${STATUS_META[c.status].className}`}
                          data-testid={`conv-status-${c.id}`}
                        >
                          {STATUS_META[c.status].label}
                        </Badge>
                      )}
                      {(() => {
                        const handlerAgent = c.assigned_agent_id ? agents.find((a) => a.id === c.assigned_agent_id) : null;
                        const paused = c.bot_paused;
                        if (paused && handlerAgent) {
                          return (
                            <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 h-4 rounded-md bg-blue-500/10 text-blue-600 border border-blue-500/30" data-testid={`handler-${c.id}`}>
                              <UserIcon size={9} weight="fill" />
                              {handlerAgent.name.split(" ")[0]}
                            </span>
                          );
                        }
                        if (paused) {
                          return (
                            <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 h-4 rounded-md bg-blue-500/10 text-blue-600 border border-blue-500/30" data-testid={`handler-${c.id}`}>
                              <UserIcon size={9} weight="fill" /> Humano
                            </span>
                          );
                        }
                        return (
                          <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0 h-4 rounded-md bg-emerald-500/10 text-emerald-600 border border-emerald-500/30" data-testid={`handler-${c.id}`}>
                            <Robot size={9} weight="fill" /> Bot
                          </span>
                        );
                      })()}
                      {c.sentiment === "negative" && <span className="text-[10px] text-red-500">😟</span>}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </ScrollArea>
        </div>

        {/* Conversation detail */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-background">
          {!detail ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <ChatCircle size={48} className="mx-auto text-muted-foreground/40 mb-3" />
                <div className="font-heading text-xl font-bold mb-1">{t("inbox.select_conv")}</div>
                <div className="text-sm text-muted-foreground">{t("inbox.no_selection")}</div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="border-b border-border p-4 flex items-center justify-between bg-card shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                    {detail.customer?.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {detail.customer?.name}
                      <ChannelBadge channel={detail.conversation?.channel} />
                      <AccountTag channel={detail.conversation?.channel} accountId={detail.conversation?.channel_account_id} testId="conv-header-account" />
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span>{detail.customer?.email || detail.customer?.phone || "—"}</span>
                      {detail.assigned_agent && (
                        <span
                          data-testid="conv-assigned-chip"
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-[10px] font-semibold uppercase tracking-wider"
                          title={`Asignado a ${detail.assigned_agent.name}${detail.assigned_agent.specialties?.length ? " · " + detail.assigned_agent.specialties.join(", ") : ""}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              detail.assigned_agent.presence === "available"
                                ? "bg-emerald-500"
                                : detail.assigned_agent.presence === "busy"
                                ? "bg-amber-500"
                                : detail.assigned_agent.presence === "away"
                                ? "bg-slate-400"
                                : "bg-rose-500"
                            }`}
                          />
                          {detail.assigned_agent.name}
                          {detail.assigned_agent.presence !== "available" && (
                            <span className="opacity-70 normal-case tracking-normal text-[9px]">
                              · {detail.assigned_agent.presence === "offline" ? "offline" : detail.assigned_agent.presence}
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {detail.conversation?.status && STATUS_META[detail.conversation.status] && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] uppercase tracking-wider ${STATUS_META[detail.conversation.status].className}`}
                      data-testid="conv-status-badge"
                    >
                      {STATUS_META[detail.conversation.status].label}
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleBot}
                    data-testid="btn-bot-toggle"
                    className={`gap-1.5 ${detail.conversation?.bot_paused ? "border-rose-500/50 text-rose-500 hover:bg-rose-500/10" : "border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"}`}
                    title={detail.conversation?.bot_paused ? "El bot está pausado — click para reactivar" : "El bot está activo — click para pausar y responder tú"}
                  >
                    {detail.conversation?.bot_paused ? <Play size={14} weight="fill" /> : <Pause size={14} weight="fill" />}
                    <Robot size={14} />
                    {detail.conversation?.bot_paused ? "Reactivar bot" : "Pausar bot"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={doSummarize} disabled={summaryLoading} data-testid="btn-summarize" className="gap-1.5">
                    <Sparkle size={14}/>{t("inbox.summarize")}
                  </Button>
                  <span title={escalatePerm.allowed ? "Escalar la conversación a un supervisor o agente específico" : escalatePerm.reason}
                    data-testid="btn-escalate-wrapper">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEscalateOpen(true)}
                      disabled={!escalatePerm.allowed}
                      data-testid="btn-escalate"
                      className="gap-1.5 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                    >
                      <WarningOctagon size={14}/>{t("inbox.escalate")}
                    </Button>
                  </span>
                  {!escalatePerm.allowed && (
                    <span className="text-[11px] text-muted-foreground self-center" data-testid="escalate-disabled-notice">{escalatePerm.reason}</span>
                  )}
                  <Button size="sm" onClick={() => updateStatus("resolved")} data-testid="btn-resolve" className="gap-1.5">
                    <CheckCircle size={14}/>{t("inbox.mark_resolved")}
                  </Button>
                </div>
              </div>

              <div className="flex flex-1 min-h-0">
                {/* Messages */}
                <div className="flex-1 flex flex-col min-w-0 min-h-0">
                  {/* Tags editor */}
                  {detail.conversation?.id && (
                    <div className="px-4 pt-3">
                      <ConversationTagsEditor
                        conversationId={detail.conversation.id}
                        initialTags={detail.conversation.tags || []}
                      />
                    </div>
                  )}
                  {detail.conversation?.reopened_at && (
                    <PriorCasesCard key={detail.conversation.id} conversationId={detail.conversation.id} reopenedAt={detail.conversation.reopened_at} />
                  )}
                  {currentCase(detail.conversation, detail.conversation?.after_hours_contact?.created_at) && detail.conversation?.after_hours_contact && (
                    <AfterHoursContactCard data={detail.conversation.after_hours_contact} />
                  )}
                  {currentCase(detail.conversation, detail.conversation?.appointment_request?.created_at) && detail.conversation?.appointment_request && (
                    <AppointmentRequestCard data={detail.conversation.appointment_request} customer={detail.customer} />
                  )}
                  {currentCase(detail.conversation, detail.conversation?.parts_quote?.escalated_at) && detail.conversation?.parts_quote && (
                    <PartsQuoteCard
                      key={detail.conversation.id}
                      conversationId={detail.conversation.id}
                      data={detail.conversation.parts_quote}
                    />
                  )}
                  {/* Structured form summary (Sprint 30B) */}
                  {detail.conversation?.last_form_type && detail.conversation?.last_form_data && (
                    <FormSummaryCard
                      formType={detail.conversation.last_form_type}
                      data={detail.conversation.last_form_data}
                      leadId={detail.conversation.lead_id || detail.lead?.id}
                      onOpenLead={(lid) => window.open(`/leads?highlight=${lid}`, "_blank")}
                    />
                  )}
                  <div ref={scrollRef} className="flex-1 overflow-auto p-6 space-y-4">
                    {/* Sprint 35B — Timeline por canal: muestra chips cuando
                       el hilo tiene mensajes de más de un canal. */}
                    {(() => {
                      const chans = Array.from(new Set((detail.messages || [])
                        .map((m) => m.channel).filter(Boolean)));
                      if (chans.length < 2) return null;
                      return (
                        <div className="flex items-center gap-2 flex-wrap sticky top-0 bg-background/80 backdrop-blur px-1 py-2 z-10 -mt-2 mb-2 border-b border-border/40" data-testid="channel-filter-bar">
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Ver canal:</span>
                          <button
                            type="button"
                            onClick={() => setChannelFilter(null)}
                            data-testid="channel-filter-all"
                            className={`px-2 py-0.5 text-[11px] rounded-full border transition ${!channelFilter ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60"}`}
                          >
                            Todos
                          </button>
                          {chans.map((ch) => (
                            <button
                              key={ch}
                              type="button"
                              onClick={() => setChannelFilter(ch === channelFilter ? null : ch)}
                              data-testid={`channel-filter-${ch}`}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border transition ${channelFilter === ch ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60"}`}
                            >
                              <ChannelBadge channel={ch} />
                              <span className="capitalize">{ch}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    {detail.messages?.filter((m) => !channelFilter || m.sender_type === "system" || m.channel === channelFilter).map((m) => {
                      const isCust = m.sender_type === "customer";
                      const isAi = m.sender_type === "ai";
                      const isSys = m.sender_type === "system";
                      if (isSys) {
                        return (
                          <div key={m.id} className="flex justify-center">
                            <div className="text-[11px] text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-full">
                              {m.content}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={m.id} className={`flex gap-2 ${isCust ? "justify-start" : "justify-end"}`}>
                          {isAi && (
                            <div className="w-8 h-8 rounded-md overflow-hidden bg-black flex-shrink-0 self-end flex items-center justify-center">
                              <SdmLogo size={30} />
                            </div>
                          )}
                          <div className={`max-w-[70%] rounded-lg p-3 text-sm ${
                            isCust ? "bg-secondary" : isAi ? "bg-primary/10 border border-primary/20" : "bg-primary text-primary-foreground"
                          }`}>
                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-70 mb-1">
                              {isCust ? <UserIcon size={10} weight="fill" /> : null}
                              {isAi ? "SDM Concierge" : (m.sender_name || m.sender_type)}
                              {m.channel && (
                                <span
                                  className="inline-flex items-center gap-0.5 ml-1 px-1 py-0.5 rounded-full bg-background/40 border border-border/40"
                                  title={`Canal: ${m.channel}`}
                                  data-testid={`msg-channel-${m.channel}`}
                                >
                                  <ChannelBadge channel={m.channel} />
                                </span>
                              )}
                            </div>
                            <div className="whitespace-pre-wrap">{linkify(m.content)}</div>
                            {Array.isArray(m.quick_replies) && m.quick_replies.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2" data-testid={`msg-quick-replies-${m.id}`}>
                                {m.quick_replies.map((q) => (
                                  <span key={q} className="rounded-full border border-current/30 px-2 py-0.5 text-[10px] opacity-80">{q}</span>
                                ))}
                              </div>
                            )}
                            {m.attachments?.filter((url) => typeof url === "string" && (url.startsWith("/api/files/") || url.startsWith("http"))).map((url) => (
                              <AttachmentPreview key={url} url={url} />
                            ))}
                            <div className="text-[10px] opacity-60 mt-1 flex items-center gap-1.5">
                              <span>{new Date(m.created_at).toLocaleTimeString()}</span>
                              {m.delivery && !isCust && !isAi && m.delivery.channel && (() => {
                                const st = m.delivery.status;
                                const isWa = m.delivery.channel === "whatsapp";
                                // WhatsApp-style receipts (✓ sent, ✓✓ delivered gray, ✓✓ read blue).
                                if (isWa) {
                                  const label = st === "read" ? "leído" : st === "delivered" ? "entregado" : st === "sent" ? "enviado" : st === "failed" ? "falló" : st;
                                  const tick = st === "sent" ? "✓" : (st === "delivered" || st === "read") ? "✓✓" : st === "failed" ? "⚠" : "…";
                                  const cls = st === "read"
                                    ? "text-sky-500"
                                    : st === "delivered"
                                      ? "text-muted-foreground"
                                      : st === "sent"
                                        ? "text-muted-foreground"
                                        : st === "failed"
                                          ? "text-rose-500"
                                          : "text-muted-foreground";
                                  return (
                                    <span
                                      className={`inline-flex items-center gap-0.5 font-bold ${cls}`}
                                      title={`${label}${m.delivery.note ? " · " + m.delivery.note : ""}`}
                                      data-testid="msg-delivery"
                                    >
                                      {tick}
                                    </span>
                                  );
                                }
                                // Non-WA channels: keep the previous pill styling.
                                return (
                                  <span
                                    className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${
                                      st === "delivered" ? "bg-emerald-500/20 text-emerald-100"
                                        : st === "queued" ? "bg-amber-500/20 text-amber-100"
                                          : st === "failed" ? "bg-rose-500/20 text-rose-100"
                                            : "bg-slate-500/20 text-slate-100"
                                    }`}
                                    title={m.delivery.note || ""}
                                    data-testid="msg-delivery"
                                  >
                                    {m.delivery.channel?.toUpperCase()} · {st}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {suggestions.length > 0 && (
                    <div className="px-6 pb-3 space-y-2 border-t border-border pt-3 shrink-0">
                      <div className="overline">{t("inbox.suggestions")}</div>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.map((s, i) => (
                          <button
                            key={i}
                            onClick={() => { setReply(s); setSuggestions([]); }}
                            data-testid={`suggestion-${i}`}
                            className="text-xs px-3 py-1.5 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors max-w-md text-left"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Composer */}
                  <div className="border-t border-border p-4 bg-card shrink-0" data-testid="composer-block">
                    {pendingAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="pending-attachments">
                        {pendingAttachments.map((f) => (
                          <AttachmentChip
                            key={f.id}
                            file={f}
                            onRemove={() => setPendingAttachments((p) => p.filter((x) => x.id !== f.id))}
                          />
                        ))}
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <Textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder={t("inbox.compose") + " · Enter para enviar, Shift+Enter para nueva línea"}
                        data-testid="composer-input"
                        rows={2}
                        className="flex-1 resize-none min-h-[48px] max-h-40"
                      />
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <div className="flex gap-1.5">
                          <AttachmentButton
                            conversationId={selectedId}
                            onUploaded={(f) => setPendingAttachments((p) => [...p, f])}
                          />
                          <MessageTemplatesPicker
                            channel={detail?.conversation?.channel}
                            lang={lang}
                            ctx={{
                              customer_name: detail?.customer?.name?.split(" ")[0] || "",
                              agent_name: (JSON.parse(localStorage.getItem("sdm_user") || "{}").name || "").split(" ")[0] || "",
                              branch: detail?.conversation?.branch || "Kennedy",
                              date: "",
                              time: "",
                              advisor_name: "",
                            }}
                            onSelect={(txt) => setReply((r) => r ? `${r}\n${txt}` : txt)}
                          />
                          <Button variant="outline" size="icon" onClick={doSuggest} disabled={suggestLoading} data-testid="btn-suggest" title="Sugerencias IA">
                            <Sparkle size={16}/>
                          </Button>
                        </div>
                        <Button onClick={send} data-testid="btn-send" className="gap-1.5 w-full">
                          <PaperPlaneRight size={14}/>{t("common.send")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right sidebar - customer */}
                <div className="w-72 border-l border-border p-5 bg-card overflow-auto shrink-0" data-testid="customer-sidebar">
                  <div className="overline mb-2">{t("inbox.customer")}</div>
                  <div className="space-y-1 text-sm mb-6">
                    <div className="font-semibold">{detail.customer?.name}</div>
                    <div className="text-muted-foreground text-xs">{detail.customer?.email}</div>
                    <div className="text-muted-foreground text-xs">{detail.customer?.phone}</div>
                    {detail.customer?.is_vip && <Badge className="bg-amber-500 text-white mt-1">VIP</Badge>}
                  </div>

                  {detail.customer?.id && (
                    <CustomerVehicles customerId={detail.customer.id}
                      refreshKey={`${detail.conversation?.appointment_request?.created_at || ""}${detail.conversation?.parts_quote?.escalated_at || ""}`} />
                  )}

                  {detail.conversation?.summary && (
                    <div className="mb-6">
                      <div className="overline mb-2">{t("inbox.summary")}</div>
                      <div className="text-xs text-muted-foreground leading-relaxed">
                        {detail.conversation.summary}
                      </div>
                    </div>
                  )}

                  {detail.lead && (
                    <div>
                      <div className="overline mb-2">Lead</div>
                      <div className="border border-border rounded-md p-3 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">{t("leads.score")}</span>
                          <span className="font-heading font-bold text-lg">{detail.lead.score}</span>
                        </div>
                        <Badge className={
                          detail.lead.intent === "high" ? "bg-emerald-500 text-white"
                            : detail.lead.intent === "medium" ? "bg-amber-500 text-white"
                            : "bg-slate-500 text-white"
                        }>{detail.lead.intent}</Badge>
                        <div className="text-xs text-muted-foreground mt-2">
                          {detail.lead.brand} {detail.lead.model}
                        </div>
                        {detail.lead.recommended_action && (
                          <div className="text-xs mt-2 pt-2 border-t border-border">
                            💡 {detail.lead.recommended_action}
                          </div>
                        )}
                      </div>
                      <NextContactPanel leadId={detail.lead.id} />
                    </div>
                  )}

                  {/* Customer history — Sprint 2 */}
                  {detail.customer?.id && (
                    <CustomerHistoryPanel
                      customerId={detail.customer.id}
                      currentConversationId={detail.conversation?.id}
                      onSelectConversation={(id) => setSelectedId(id)}
                      lang="es"
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <EscalateDialog
        open={escalateOpen}
        onOpenChange={setEscalateOpen}
        conversationId={selectedId}
        onEscalated={onEscalated}
      />
    </>
  );
}
